import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines, EASE } from "@/components/Reveal";

export const PageHero = ({ eyebrow, lines, image, alt, children, testid = "page-hero", softFade = false }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-heritage-deep pt-[72px] lg:bg-ivory lg:pt-[88px]" data-testid={testid}>
      <div className="relative h-[78svh] min-h-[520px] overflow-hidden lg:h-[64vh] lg:min-h-[500px]">
      {image.video ? (
        <motion.video poster={image.src} autoPlay muted loop playsInline preload="auto" aria-label={alt} data-testid={`${testid}-video`} style={{ y, scale }} className="absolute inset-0 h-full w-full object-cover">
          {image.video.map((v) => <source key={v.src} src={v.src} type={v.type} />)}
        </motion.video>
      ) : (
        <motion.img src={image.src} alt={alt} style={{ y, scale, objectPosition: image.position }} className="absolute inset-0 h-full w-full object-cover" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-heritage-deep via-heritage-deep/40 to-heritage-deep/10 lg:hidden" />
      <div className={`absolute inset-y-0 left-0 hidden bg-gradient-to-r lg:block ${softFade ? "w-[52%] from-ivory/95 via-ivory/60 to-transparent" : "w-[65%] from-ivory via-ivory/80 to-transparent"}`} />
      <div className="relative mx-auto flex h-full max-w-[1440px] flex-col justify-end px-5 pb-14 md:px-10 lg:justify-center lg:pb-0 lg:pt-16">
        <motion.p
          className={`mb-5 font-caps text-[12px] tracking-[0.34em] uppercase text-gold-light ${softFade ? "lg:font-semibold lg:text-heritage" : "lg:text-gold-deep"}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
        >
          {eyebrow}
        </motion.p>
        <MaskLines lines={lines} delay={0.3} className="max-w-2xl font-caps text-4xl leading-[1.08] text-ivory sm:text-5xl lg:text-[3.4rem] lg:text-heritage" testid={`${testid}-title`} />
        {children && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-8">
            {children}
          </motion.div>
        )}
      </div>
      </div>
      <div className="h-px bg-gradient-to-r from-gold/20 via-gold to-gold/20" />
    </section>
  );
};
